/**
 * The member's two platform slots, as the enrolment and reset paths use them
 * (spec section 6.2 "two slots per member"; decision 40's read-back).
 *
 * Three verbs over the adapter's `put` / `get` / `remove`:
 *
 *   writeSlot        put the copy, then prove the medium holds it
 *   readSlot         what a slot holds, parsed, or null
 *   neutraliseSlot   overwrite with a tombstone (must land), then remove
 *                    (best effort) — the #1608 two-step, for either slot
 *
 * Pure JS over `platform-copy.js`; the adapter itself is handed in, so this
 * module drags no native plugin into whoever imports it.
 */
import {
  PLATFORM_COPY_SLOT,
  parsePlatformCopy,
  serialisePlatformCopy,
  serialisePlatformTombstone,
} from '@/utils/courses-crypto/platform-copy';

export { PLATFORM_COPY_SLOT };

/**
 * @param {Uint8Array} a
 * @param {Uint8Array} b
 */
function bytesEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a[i] ^ b[i];
  return diff === 0;
}

/**
 * What `slot` holds for this member, or null for an absent copy, a tombstone,
 * or a copy this app did not write for this member.
 *
 * @param {{ adapter: object, memberId: number, slot: string }} args
 * @returns {Promise<{ keyId: string, mk: Uint8Array } | null>}
 */
export async function readSlot({ adapter, memberId, slot }) {
  return parsePlatformCopy(await adapter.get(memberId, slot), memberId);
}

/**
 * Whether `slot` reads back as a copy of exactly this key.
 *
 * THE WEB BUILD HAS NO PLATFORM COPY (spec section 6.10): its adapter's `put`
 * is a no-op and its `get` is null, so there is nothing to read back and
 * nothing the read-back could prove. An absent copy on `web` is therefore not
 * a failed proof — the key store's own `persistent` gate is the durability
 * proof there. On a native adapter an absent or mismatched copy is exactly
 * the failure decision 40 asks to be caught before the POST.
 *
 * @param {{ adapter: object, memberId: number, keyId: string, mk: Uint8Array, slot: string }} args
 * @returns {Promise<boolean>}
 */
export async function slotHolds({ adapter, memberId, keyId, mk, slot }) {
  const raw = await adapter.get(memberId, slot);
  if (raw === null && adapter.kind === 'web') return true;
  const copy = parsePlatformCopy(raw, memberId);
  return !!copy && copy.keyId === keyId && bytesEqual(copy.mk, mk);
}

/**
 * Writes the member's copy into `slot` and proves the medium holds it.
 *
 * @param {{ adapter: object, memberId: number, keyId: string, mk: Uint8Array, slot: string }} args
 * @returns {Promise<boolean>} true when the copy read back; false when the
 *   write resolved but the slot does not hold it
 * @throws whatever the adapter's `put` or `get` throws
 */
export async function writeSlot({ adapter, memberId, keyId, mk, slot }) {
  await adapter.put(memberId, serialisePlatformCopy({ memberId, keyId, mk }), slot);
  return slotHolds({ adapter, memberId, keyId, mk, slot });
}

/**
 * Overwrite `slot` with a tombstone, then remove it. Unconditional — never
 * behind `isEligible()`, whose cached answer any earlier throw sets false —
 * and the overwrite is what has to land: a tombstone parses as "no copy" on
 * every build that has ever shipped this format, so a delete that then fails
 * costs nothing. Three outcomes and only one of them is bad: overwrite ok and
 * remove ok is clean; overwrite ok and remove failed is ALSO clean; a failed
 * overwrite is what the caller is told about.
 *
 * @param {{ adapter: object, memberId: number, slot: string }} args
 * @returns {Promise<boolean>} whether the OVERWRITE resolved — not whether the removal did
 */
export async function neutraliseSlot({ adapter, memberId, slot }) {
  try {
    await adapter.put(memberId, serialisePlatformTombstone(memberId), slot);
  } catch (error) {
    console.warn('[CoursesE2EE] The platform copy of the key was not overwritten:', error);
    return false;
  }
  try {
    await adapter.remove(memberId, slot);
  } catch (error) {
    // Tidiness only. The slot the next launch reads already says "no key".
    console.warn('[CoursesE2EE] The platform copy was overwritten but not removed:', error);
  }
  return true;
}
